import React, { useState } from 'react';
import { Container, Typography, Button } from '@sm/wds-react';
import { Slide } from '@sm/wds-motion';
import { IconX } from '@sm/wds-icons';
import {
  TopBarFullThemes,
  TopBarFullImagePositions,
  TopBarFullCtaColors,
  TopBarFullImageSizes,
} from './TopBarFullEnums';
import TopBarFullSkeletonLoader from './TopBarFullSkeleton';
import './top-bar-full.scss';
import { useContentModuleEvent } from '../../hooks/useContentModuleEvent';
import { ContentModuleEvent } from '../../types/enums';
import { ContentModuleProperties } from '../../types/types';

export type TopBarFullProps = {
  headline: string;
  message: string;
  image: () => React.ReactElement;
  ctaText: string;
  ctaLink: string;
  theme?: TopBarFullThemes;
  imageSize?: TopBarFullImageSizes;
  imagePosition?: TopBarFullImagePositions;
  ctaNewTab?: boolean;
  ctaColor?: TopBarFullCtaColors;
  dismissable?: boolean;
  loading?: boolean;
};

const marginTopSize = (
  imageSize: TopBarFullImageSizes,
  imagePosition: TopBarFullImagePositions
): string => {
  let mt = '4';
  if (imagePosition === TopBarFullImagePositions.low) {
    mt = imageSize === TopBarFullImageSizes.small ? '6' : '5';
  }
  return mt;
};

export const TopBarFull: React.FC<TopBarFullProps> = ({
  headline,
  message,
  image,
  ctaText,
  ctaLink,
  theme = TopBarFullThemes.white,
  imageSize = TopBarFullImageSizes.small,
  imagePosition = TopBarFullImagePositions.high,
  ctaNewTab = false,
  ctaColor = TopBarFullCtaColors.alt,
  dismissable = false,
  loading = false,
}) => {
  const [show, setShow] = useState(true);
  const ImageComponent = image;
  const recordModuleEvent = useContentModuleEvent();
  const handleDismiss = () => {
    setShow(false);
    recordModuleEvent(ContentModuleEvent.MODULE_DISMISSED);
  };
  const handleCtaClick = (e: Event, ctaLink: string) => {
    e.preventDefault();
    const properties: ContentModuleProperties = {
      destination: ctaLink,
    };
    recordModuleEvent(ContentModuleEvent.CTA_CLICKED, properties);
    if (ctaNewTab) {
      window.open(ctaLink);
    } else {
      window.location.assign(ctaLink);
    }
  };

  return (
    <>
      {loading ? (
        <TopBarFullSkeletonLoader />
      ) : (
        <Slide direction="down" in={show}>
          <Container
            className={`sm-top-bar-full--${theme}`}
            data-testid="theme-wrapper"
          >
            {dismissable && (
              <Container className="sm-top-bar-full__button-wrapper">
                <Button
                  color={theme === TopBarFullThemes.white ? 'black' : theme}
                  data-testid="dismiss-button"
                  onClick={handleDismiss}
                  variant={theme === TopBarFullThemes.white ? 'text' : 'solid'}
                >
                  <IconX />
                </Button>
              </Container>
            )}
            <Container flex flexDirection="row" flexJustify="center">
              <Container
                className={`sm-top-bar-full--${imageSize}`}
                data-testid="image-wrapper"
                mt={marginTopSize(imageSize, imagePosition)}
                mr="5"
              >
                <ImageComponent />
              </Container>
              <Container
                flex
                flexDirection="column"
                flexAlign="center"
                m="2"
                pb="1"
              >
                <Container>
                  <Typography
                    className={`sm-top-bar-full--${theme}__text`}
                    component="section"
                    variant="sectionTitle"
                  >
                    <strong data-testid="headline">{headline}</strong>
                  </Typography>
                  <Container mt="1">
                    <Typography
                      className={`sm-top-bar-full--${theme}__text`}
                      component="div"
                      variant="body"
                    >
                      {message}
                    </Typography>
                  </Container>
                  <Container mt="3">
                    <Button
                      onClick={(e: Event) => handleCtaClick(e, ctaLink)}
                      href={ctaLink}
                      target={ctaNewTab ? '_blank' : undefined}
                      rel={ctaNewTab ? 'noreferrer noopener' : undefined}
                      size="sm"
                      color={ctaColor}
                    >
                      {ctaText}
                    </Button>
                  </Container>
                </Container>
              </Container>
            </Container>
          </Container>
        </Slide>
      )}
    </>
  );
};

export default TopBarFull;
